/**
 * Translation data from the scripture server.
 *
 * Since this affects the app's routing, it greatly simplifies the code to have this hard-coded here rather than
 * requested from the scripture server asynchronously at runtime.
 */

import type { StandaloneTranslationData } from "@rsc/scripture-model";
import data from "./data/allTranslations.json";
import { navigationTranslationIds } from "./navigationTranslationIds";
import { defaultLocale } from "~/i18n/locales";

/**
 * All translations, keyed by translation ID.
 */
export const allTranslations: Record<string, StandaloneTranslationData> =
  Object.fromEntries(
    (data as StandaloneTranslationData[]).map((translation) => [
      translation.tid,
      translation,
    ]),
  );

/**
 * All translations as an array.
 */
export const allTranslationsArray: StandaloneTranslationData[] =
  Object.values(allTranslations);

/** Translations offered in navigation and search; metadata refreshes cannot enable editions. */
export const availableTranslationsArray = allTranslationsArray.filter((t) =>
  navigationTranslationIds.includes(t.tid),
);

/** The IDs available to navigation and search, excluding missing catalogue entries. */
export const availableTids: string[] = availableTranslationsArray.map(
  (t) => t.tid,
);

/**
 * Get the locale code of a bible translation.
 *
 * @param tid
 *   The ID of the translation.
 *
 * @return
 *   The locale of the translation.
 */
export function getTidLocale(tid?: string): string {
  return allTranslations[tid ?? ""]?.language ?? defaultLocale;
}

/** Disambiguate editions sharing a display name, while retaining familiar names elsewhere. */
export function translationLabel(
  translation: StandaloneTranslationData,
): string {
  return allTranslationsArray.filter((t) => t.name === translation.name)
    .length > 1
    ? `${translation.name} (${translation.year})`
    : translation.name;
}
